import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { X, Star, ShoppingCart, Plus, Minus, MessageCircle, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackViewContent } from '../utils/fbpixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetailModal = ({ product, isOpen, onClose, onAddToCart }) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [customUrl, setCustomUrl] = useState('');
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ user_name: '', user_email: '', rating: 5, review_text: '' });

  useEffect(() => {
    if (product && isOpen) {
      fetchReviews();
      fetchReviewSummary();
      // Meta Pixel — ViewContent (deduped per product id inside the helper)
      trackViewContent({
        id: product.id,
        name: product.title,
        price: product.sale_price || product.price,
      });
    }
  }, [product, isOpen]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reviews/product/${product.id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchReviewSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reviews/product/${product.id}/summary`);
      setReviewSummary(response.data);
    } catch (error) {
      console.error('Error fetching review summary:', error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/reviews/`, {
        product_id: product.id,
        ...newReview
      });
      toast.success('Review submitted successfully!');
      setShowReviewForm(false);
      setNewReview({ user_name: '', rating: 5, review_text: '' });
      fetchReviews();
      fetchReviewSummary();
    } catch (error) {
      toast.error('Failed to submit review');
    }
  };

  const handleAddToCart = () => {
    // Validate URL is provided
    if (!customUrl.trim()) {
      toast.error('Please enter the URL you want programmed into the NFC tag');
      return;
    }
    
    // Basic URL validation
    if (!customUrl.startsWith('http://') && !customUrl.startsWith('https://')) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    
    onAddToCart(product, quantity, customUrl);
    setQuantity(1);
    setCustomUrl('');
    onClose();
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`}
      />
    ));
  };

  if (!isOpen || !product) return null;

  const price = product.sale_price || product.price;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="bg-gray-900 border-gray-800 max-w-5xl w-full relative max-h-[90vh] overflow-y-auto">
          <CardContent className="p-0">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-1.5 bg-gray-800 hover:bg-gray-700 rounded-full text-white z-10"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="grid md:grid-cols-2 gap-6 p-6">
              {/* Left: Images */}
              <div>
                {/* Main Image - Reduced size */}
                <div className="bg-white rounded-lg overflow-hidden aspect-square mb-3">
                  <img
                    src={
                      product.images?.[selectedImage]
                        ? product.images[selectedImage].startsWith('http')
                          ? product.images[selectedImage]
                          : `${API_URL}${product.images[selectedImage]}`
                        : 'https://via.placeholder.com/500'
                    }
                    alt={product.title}
                    className="w-full h-full object-contain"
                  />
                </div>
                
                {/* Thumbnail Images */}
                {product.images?.length > 1 && (
                  <div className="grid grid-cols-4 gap-2">
                    {product.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(idx)}
                        className={`aspect-square rounded-lg overflow-hidden border-2 bg-white ${
                          selectedImage === idx ? 'border-blue-500' : 'border-gray-300'
                        }`}
                      >
                        <img 
                          src={img.startsWith('http') ? img : `${API_URL}${img}`} 
                          alt={`${product.title} ${idx + 1}`} 
                          className="w-full h-full object-contain" 
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

            {/* Right: Details */}
            <div>
              <div className="mb-3">
                <span className="text-xs text-gray-400 uppercase tracking-wide">{product.category}</span>
                <h2 className="text-2xl font-bold text-white mt-1">{product.title}</h2>
              </div>

              {/* Rating Summary */}
              {reviewSummary && reviewSummary.total_reviews > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex">{renderStars(Math.round(reviewSummary.average_rating))}</div>
                  <span className="text-gray-300 text-sm">{reviewSummary.average_rating}</span>
                  <span className="text-gray-500 text-sm">({reviewSummary.total_reviews} reviews)</span>
                </div>
              )}

              {/* Price */}
              <div className="mb-4">
                {product.sale_price ? (
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-bold text-white">${product.sale_price}</span>
                    <span className="text-xl text-gray-500 line-through">${product.price}</span>
                    <span className="bg-red-500 text-white px-2 py-0.5 rounded text-xs">SALE</span>
                  </div>
                ) : (
                  <span className="text-3xl font-bold text-white">${product.price}</span>
                )}
              </div>

              {/* Description - Scrollable box so image and key details stay visible */}
              <div
                className="text-gray-300 text-sm mb-4 whitespace-pre-line leading-relaxed max-h-40 overflow-y-auto pr-3 border-l-2 border-blue-500/40 pl-3 custom-scrollbar"
                data-testid="product-description"
              >
                {product.full_description || product.description || product.short_description}
              </div>

              {/* Features - Show all */}
              {product.features?.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-white mb-2">Features:</h3>
                  <ul className="space-y-1">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="text-gray-300 text-sm flex items-start gap-2">
                        <Star className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Stock Status */}
              <div className="mb-3">
                <span className={`text-sm font-medium ${
                  product.stock_status === 'in_stock' ? 'text-green-400' : 
                  product.stock_status === 'pre_order' ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {product.stock_status === 'in_stock' && '✓ In Stock'}
                  {product.stock_status === 'out_of_stock' && '✗ Out of Stock'}
                  {product.stock_status === 'pre_order' && '⏳ Pre-Order'}
                </span>
              </div>

              {/* NFC Programming URL Input */}
              <div className="mb-4 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <label className="block text-sm font-medium text-white mb-2">
                  Enter the URL You Want People to Visit When Tapped *
                </label>
                <input
                  type="url"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  placeholder="Examples: https://yourwebsite.com, https://instagram.com/username"
                  className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
                <p className="text-xs text-gray-400 mt-2">
                  This URL will be programmed into your NFC tag. Anyone who taps it will be directed to this link.
                </p>
              </div>

              {/* Quantity Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Quantity:</label>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                    >
                      <Minus className="w-4 h-4 text-gray-300" />
                    </button>
                    <span className="text-white font-medium w-10 text-center">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                    >
                      <Plus className="w-4 h-4 text-gray-300" />
                    </button>
                  </div>
                  <span className="text-gray-400 text-sm">Total: ${(price * quantity).toFixed(2)}</span>
                </div>
              </div>

              {/* Add to Cart Button */}
              <Button
                onClick={handleAddToCart}
                disabled={product.stock_status === 'out_of_stock'}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-4 text-base mb-4"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>

              {/* Reviews Section - Compact */}
              <div className="border-t border-gray-800 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    Reviews
                  </h3>
                  <Button
                    onClick={() => setShowReviewForm(!showReviewForm)}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    Write Review
                  </Button>
                </div>

                {/* Review Form */}
                {showReviewForm && (
                  <form onSubmit={handleSubmitReview} className="bg-gray-800 rounded-lg p-3 mb-3">
                    <input
                      type="text"
                      placeholder="Your Name"
                      value={newReview.user_name}
                      onChange={(e) => setNewReview({ ...newReview, user_name: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-gray-700 text-white rounded mb-2"
                      required
                    />
                    <input
                      type="email"
                      placeholder="Your Email"
                      value={newReview.user_email}
                      onChange={(e) => setNewReview({ ...newReview, user_email: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-gray-700 text-white rounded mb-2"
                      required
                    />
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-gray-300 text-sm">Rating:</span>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setNewReview({ ...newReview, rating: star })}
                        >
                          <Star className={`w-5 h-5 ${star <= newReview.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`} />
                        </button>
                      ))}
                    </div>
                    <textarea
                      placeholder="Your Review (optional)"
                      value={newReview.review_text}
                      onChange={(e) => setNewReview({ ...newReview, review_text: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-gray-700 text-white rounded mb-2 h-20"
                    />
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" className="bg-blue-500 hover:bg-blue-600 text-xs">Submit</Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => setShowReviewForm(false)} className="text-xs">Cancel</Button>
                    </div>
                  </form>
                )}

                {/* Reviews List - Compact */}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {reviews.length === 0 ? (
                    <p className="text-gray-500 text-center text-sm py-3">No reviews yet</p>
                  ) : (
                    reviews.slice(0, 3).map((review) => (
                      <div key={review.id} className="bg-gray-800 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white text-sm">{review.user_name}</span>
                            {review.is_verified_purchase && (
                              <span className="flex items-center gap-1 text-xs text-green-400">
                                <ShieldCheck className="w-3 h-3" />
                                Verified
                              </span>
                            )}
                          </div>
                          <div className="flex">{renderStars(review.rating)}</div>
                        </div>
                        {review.review_text && <p className="text-gray-300 text-xs line-clamp-2">{review.review_text}</p>}
                        <span className="text-xs text-gray-500 mt-1 block">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default ProductDetailModal;